import { FC, ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'

/**
 * UnauthGuard component props
 */
interface UnauthGuardProps {
  /** Content to render if NOT authenticated */
  children: ReactNode
  /** Redirect path if authenticated */
  redirectTo?: string
}

/**
 * UnauthGuard - Shows content for unauthenticated users only
 * Redirects authenticated users to specified path (default: /chat)
 *
 * Used for landing page, auth page, etc.
 *
 * @example
 * ```tsx
 * <Route path="/" element={
 *   <UnauthGuard redirectTo="/chat">
 *     <LandingPage />
 *   </UnauthGuard>
 * } />
 * ```
 */
export const UnauthGuard: FC<UnauthGuardProps> = ({
  children,
  redirectTo = '/chat'
}) => {
  // Reuse the global bootstrap result from authStore — UnauthGuard used to
  // run its own check, which raced AuthGuard on shared routes and double-hit
  // /api/auth/me. With cookie-rehydrated auth, the store is the single
  // source of truth and bootstrap() is fired once at App mount.
  const { isAuthenticated } = useAuthStore()

  // Redirect if authenticated
  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />
  }

  // Render content for unauthenticated visitors — INCLUDING while bootstrap()
  // is still in flight.
  //
  // ⚠️ This deliberately does NOT mirror `AuthGuard`, which blocks on
  // `isLoading` and must keep doing so. The asymmetry is the point: AuthGuard
  // decides whether to show PROTECTED content to a possibly-anonymous visitor,
  // where rendering early would leak. UnauthGuard decides whether to show
  // PUBLIC marketing copy to a possibly-signed-in visitor, where rendering
  // early leaks nothing and costs a signed-in user ~200ms of landing page
  // before the redirect fires.
  //
  // It used to render a spinner while `isLoading`, and that made the landing
  // page un-prerenderable: `bootstrap()` is an effect, effects do not run
  // during a static prerender, so `isLoading` stayed `true` forever and the
  // build emitted 378 bytes of spinner as the entire "prerendered" homepage.
  // Forcing the store into a settled state for the prerender would have fixed
  // the bytes and broken hydration instead — the client's first render is also
  // `isLoading: true`, so the server HTML and the first client render would
  // have disagreed and the user would have seen content, then a spinner, then
  // content again. Rendering children in both cases makes the two agree by
  // construction, which is the property that actually matters.
  return <>{children}</>
}
